import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, Pill } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, Mail, Mailbox, Tv, Play, Pause, RefreshCw, Route as RouteIcon, Clock, Check, X, Loader2 } from "lucide-react";

const STEP_META = {
  email: { label: "Email", icon: Mail, color: "text-cyan-300 border-cyan-400/30 bg-cyan-400/10" },
  direct_mail: { label: "Direct Mail", icon: Mailbox, color: "text-amber-300 border-amber-400/30 bg-amber-400/10" },
  ctv: { label: "CTV / Media", icon: Tv, color: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" },
};

const STEP_STATUS = {
  pending: { pill: "Draft", text: "text-slate-400" },
  scheduled: { pill: "Pending", text: "text-amber-300" },
  executed: { pill: "Active", text: "text-emerald-300" },
  skipped: { pill: "Paused", text: "text-slate-400" },
  failed: { pill: "Error", text: "text-rose-300" },
};

const JOURNEY_PILL = { draft: "Draft", running: "Active", paused: "Paused", completed: "Active" };

export default function Journeys() {
  const [journeys, setJourneys] = useState([]);
  const [assets, setAssets] = useState({ emails: [], mailers: [], campaigns: [] });
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [reports, setReports] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    api.get("/journeys").then(r => setJourneys(r.data)).catch(() => {});
    api.get("/journeys/assets").then(r => setAssets(r.data)).catch(() => {});
    api.get("/journeys/templates").then(r => setTemplates(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const err = (e, fb) => toast.error(errMsg(e, fb));

  const applyTemplate = async (key) => {
    setBusy(`tpl-${key}`);
    try {
      const { data } = await api.post(`/journeys/templates/${key}`);
      toast.success("Journey created — assets drafted and sequenced. Review, then launch.");
      setChecklist(data.checklist || []);
      setEditing(data.journey);
      load();
    } catch (e) { err(e, "Could not create from template"); }
    finally { setBusy(""); }
  };

  const tplDays = (steps) => { let d = 0; return steps.map(s => { d += s.delay_days || 0; return d; }); };

  const options = (type) => (
    { email: assets.emails, direct_mail: assets.mailers, ctv: assets.campaigns }[type] || []
  );

  const addStep = (type) => setEditing(j => ({
    ...j, steps: [...j.steps, { type, ref_id: "", delay_days: j.steps.length === 0 ? 0 : 3 }],
  }));
  const setStep = (i, patch) => setEditing(j => ({
    ...j, steps: j.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
  }));
  const removeStep = (i) => setEditing(j => ({ ...j, steps: j.steps.filter((_, idx) => idx !== i) }));

  const save = async (silent = false) => {
    const payload = { name: editing.name, description: editing.description || "", steps: editing.steps };
    try {
      let saved;
      if (editing.id) saved = (await api.put(`/journeys/${editing.id}`, payload)).data;
      else saved = (await api.post("/journeys", payload)).data;
      setEditing(saved);
      if (!silent) toast.success("Journey saved");
      load();
      return saved;
    } catch (e) { err(e, "Could not save journey"); return null; }
  };

  const launch = async () => {
    const saved = await save(true);
    if (!saved) return;
    if (!window.confirm("Launch this journey? Step 1 executes immediately; later steps fire on schedule.")) return;
    setBusy("launch");
    try {
      const { data } = await api.post(`/journeys/${saved.id}/launch`);
      setEditing(null);
      toast.success("Journey launched");
      load();
      fetchReport(data.id);
    } catch (e) { err(e, "Launch failed"); }
    finally { setBusy(""); }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/journeys/${id}/${action}`);
      toast.success(action === "pause" ? "Journey paused" : "Journey resumed");
      load();
    } catch (e) { err(e, "Action failed"); }
  };

  const fetchReport = async (id) => {
    try {
      const { data } = await api.get(`/journeys/${id}/report`);
      setReports(r => ({ ...r, [id]: data }));
    } catch { /* noop */ }
  };

  let cumDay = 0;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Omnichannel Orchestration"
        title="Journeys"
        subtitle="Sequence email, direct mail and CTV into one coordinated flow — each touch fires automatically on schedule."
        action={!editing && (
          <Btn onClick={() => { setEditing({ name: "", description: "", steps: [] }); setChecklist([]); }} data-testid="new-journey-btn">
            <Plus className="w-4 h-4" /> New journey
          </Btn>
        )}
      />

      {/* Builder */}
      {editing && (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          <Card className="xl:col-span-3" data-testid="journey-builder">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold">{editing.id ? "Edit journey" : "New journey"}</h3>
              <button onClick={() => { setEditing(null); setChecklist([]); }} className="text-xs text-slate-400 hover:text-white">← Back to list</button>
            </div>
            {checklist.length > 0 && (
              <div className="mb-4 rounded-lg border border-amber-400/25 bg-amber-400/[0.05] p-3.5" data-testid="template-checklist">
                <div className="text-sm font-semibold text-amber-200 mb-1">Before you launch</div>
                {checklist.map(c => <div key={c} className="text-xs text-amber-200/80 leading-relaxed">· {c}</div>)}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Input label="Journey name" value={editing.name} onChange={e => setEditing(j => ({ ...j, name: e.target.value }))} data-testid="journey-name-input" />
              <Input label="Description" value={editing.description || ""} onChange={e => setEditing(j => ({ ...j, description: e.target.value }))} />
            </div>

            <div className="mt-5 space-y-3">
              {editing.steps.map((s, i) => {
                const meta = STEP_META[s.type];
                const Icon = meta.icon;
                return (
                  <div key={i} className={`rounded-lg border p-3 ${meta.color.split(" ").slice(1).join(" ")}`} data-testid={`journey-step-${i}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${meta.color.split(" ")[0]}`}>
                        <Icon className="w-4 h-4" /> Step {i + 1} · {meta.label}
                      </span>
                      <div className="flex items-center gap-2 ml-auto">
                        {i > 0 && (
                          <label className="flex items-center gap-1.5 text-xs text-slate-400">
                            <Clock className="w-3.5 h-3.5" /> wait
                            <input type="number" min="0" step="0.5" value={s.delay_days}
                              onChange={e => setStep(i, { delay_days: parseFloat(e.target.value) || 0 })}
                              className="w-16 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs" data-testid={`journey-step-delay-${i}`} />
                            days
                          </label>
                        )}
                        <button onClick={() => removeStep(i)} className="text-slate-500 hover:text-rose-300 p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <select value={s.ref_id} onChange={e => setStep(i, { ref_id: e.target.value })}
                      className="mt-2 w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/50" data-testid={`journey-step-ref-${i}`}>
                      <option value="">Select {meta.label.toLowerCase()}…</option>
                      {options(s.type).map(o => (
                        <option key={o.id} value={o.id}>{o.name || o.subject} ({o.status})</option>
                      ))}
                    </select>
                  </div>
                );
              })}
              {editing.steps.length === 0 && (
                <div className="text-center py-6 text-sm text-slate-500 border border-dashed border-white/10 rounded-lg">Add your first touch below.</div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Btn variant="secondary" onClick={() => addStep("email")} data-testid="journey-add-step-email"><Mail className="w-3.5 h-3.5" /> + Email</Btn>
              <Btn variant="secondary" onClick={() => addStep("direct_mail")} data-testid="journey-add-step-dm"><Mailbox className="w-3.5 h-3.5" /> + Direct Mail</Btn>
              <Btn variant="secondary" onClick={() => addStep("ctv")} data-testid="journey-add-step-ctv"><Tv className="w-3.5 h-3.5" /> + CTV / Media</Btn>
            </div>

            <div className="mt-5 pt-4 border-t border-white/10 flex gap-2 justify-end">
              <Btn variant="secondary" onClick={() => save()} data-testid="journey-save-btn">Save draft</Btn>
              <Btn onClick={launch} disabled={busy === "launch" || editing.steps.length === 0 || editing.steps.some(s => !s.ref_id)} data-testid="journey-launch-btn">
                {busy === "launch" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Launch journey
              </Btn>
            </div>
          </Card>

          {/* Timeline preview */}
          <Card className="xl:col-span-2" data-testid="journey-timeline-preview">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-4">Timeline preview</div>
            <div className="space-y-0">
              {editing.steps.map((s, i) => {
                if (i === 0) cumDay = 0; else cumDay += parseFloat(s.delay_days) || 0;
                const meta = STEP_META[s.type];
                const Icon = meta.icon;
                const asset = options(s.type).find(o => o.id === s.ref_id);
                return (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-9 h-9 rounded-full border flex items-center justify-center ${meta.color}`}><Icon className="w-4 h-4" /></div>
                      {i < editing.steps.length - 1 && <div className="w-px flex-1 bg-white/10 my-1" style={{ minHeight: "24px" }} />}
                    </div>
                    <div className="pb-6">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Day {cumDay}</div>
                      <div className="text-sm text-white font-medium">{meta.label}</div>
                      <div className="text-xs text-slate-400">{asset ? (asset.name || asset.subject) : "— pick an asset —"}</div>
                    </div>
                  </div>
                );
              })}
              {editing.steps.length === 0 && <div className="text-sm text-slate-500">Your sequenced touches will appear here.</div>}
            </div>
          </Card>
        </div>
      )}

      {/* One-tap templates */}
      {!editing && templates.length > 0 && (
        <div data-testid="journey-templates">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">One-tap templates</div>
          <div className="grid md:grid-cols-3 gap-4">
            {templates.map(t => {
              const days = tplDays(t.steps);
              return (
                <Card key={t.key} data-testid={`journey-template-${t.key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-display font-bold">{t.name}</div>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-bold uppercase shrink-0">{t.badge}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{t.description}</p>
                  <div className="mt-3 space-y-1.5">
                    {t.steps.map((s, i) => {
                      const meta = STEP_META[s.type];
                      const Icon = meta.icon;
                      return (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <span className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${meta.color}`}><Icon className="w-3 h-3" /></span>
                          <span className="text-[10px] text-slate-500 w-11 shrink-0">Day {days[i]}</span>
                          <span className="text-slate-300 truncate">{s.label}</span>
                        </div>
                      );
                    })}
                  </div>
                  <Btn className="mt-4 w-full justify-center" onClick={() => applyTemplate(t.key)}
                    disabled={busy === `tpl-${t.key}`} data-testid={`use-template-${t.key}`}>
                    {busy === `tpl-${t.key}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Use template
                  </Btn>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* List */}
      {!editing && (
        <div className="space-y-3" data-testid="journeys-list">
          {journeys.map(j => {
            const rep = reports[j.id];
            return (
              <Card key={j.id} data-testid={`journey-card-${j.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400/15 to-blue-500/5 border border-cyan-400/25 flex items-center justify-center">
                      <RouteIcon className="w-5 h-5 text-cyan-300" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-display font-bold text-white">{j.name}</span>
                        <Pill status={JOURNEY_PILL[j.status] || "Draft"}>{j.status}</Pill>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {j.progress?.total || 0} steps · {j.progress?.done || 0} completed
                        {j.started_at && <> · started {j.started_at.slice(0, 10)}</>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {j.status === "draft" && <Btn variant="secondary" onClick={() => setEditing(j)} data-testid={`journey-edit-${j.id}`}>Edit</Btn>}
                    {j.status !== "draft" && (
                      <Btn variant="secondary" onClick={() => fetchReport(j.id)} data-testid={`journey-report-btn-${j.id}`}>
                        <RefreshCw className="w-3.5 h-3.5" /> Timeline
                      </Btn>
                    )}
                    {j.status === "running" && <Btn variant="secondary" onClick={() => act(j.id, "pause")} data-testid={`journey-pause-${j.id}`}><Pause className="w-3.5 h-3.5" /> Pause</Btn>}
                    {j.status === "paused" && <Btn variant="secondary" onClick={() => act(j.id, "resume")} data-testid={`journey-resume-${j.id}`}><Play className="w-3.5 h-3.5" /> Resume</Btn>}
                    <button onClick={async () => { if (window.confirm("Delete this journey?")) { await api.delete(`/journeys/${j.id}`); load(); } }}
                      className="text-slate-500 hover:text-rose-300 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>

                {rep && (
                  <div className="mt-4 pt-4 border-t border-white/5" data-testid={`journey-timeline-${j.id}`}>
                    {rep.steps.map((s, i) => {
                      const meta = STEP_META[s.type];
                      const Icon = meta.icon;
                      const st = STEP_STATUS[s.status] || STEP_STATUS.pending;
                      return (
                        <div key={i} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            <div className={`w-8 h-8 rounded-full border flex items-center justify-center ${meta.color}`}>
                              {s.status === "executed" ? <Check className="w-3.5 h-3.5" /> : s.status === "failed" ? <X className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                            </div>
                            {i < rep.steps.length - 1 && <div className="w-px flex-1 bg-white/10 my-1" style={{ minHeight: "16px" }} />}
                          </div>
                          <div className="pb-4 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium text-sm">{meta.label} — {s.ref_name}</span>
                              <span className={`${st.text} uppercase tracking-wider text-[10px] font-semibold`}>{s.status}</span>
                            </div>
                            <div className="text-slate-500 mt-0.5">
                              {s.due_at && <>due {s.due_at.slice(0, 16).replace("T", " ")} UTC</>}
                              {s.note && <> · {s.note}</>}
                            </div>
                            {s.result && (
                              <div className="text-slate-400 mt-0.5">
                                {s.type === "email" && <>{s.result.sent ?? 0} sent · {s.result.opened ?? "—"} opens · {s.result.clicked ?? "—"} clicks</>}
                                {s.type === "direct_mail" && <>{Number(s.result.quantity || 0).toLocaleString()} pieces · {s.result.scans ?? 0} QR scans · {s.result.status}</>}
                                {s.type === "ctv" && <>campaign {s.result.status} · budget ${Number(s.result.budget || 0).toLocaleString()}</>}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
          {journeys.length === 0 && <div className="text-center py-14 text-sm text-slate-500">No journeys yet — sequence your first omnichannel flow.</div>}
        </div>
      )}
    </div>
  );
}
